import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { coAdmins as coAdminsApi } from '@/lib/api';
import { getStoredUser } from '@/lib/auth';
import { Loader2, Shield, CheckCircle, XCircle } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

export default function AcceptCoAdminInvite() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const user = getStoredUser();

  useEffect(() => {
    const load = async () => {
      if (!token) { setError('This invite link is invalid.'); setLoading(false); return; }
      try {
        const res = await coAdminsApi.getInvite(token);
        setInvite(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'This invite could not be found.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await coAdminsApi.acceptInvite(token);
      toast.success(`You're now a co-admin of ${res.data.school_name}.`);
      navigate('/employer');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not accept the co-admin invite.');
    } finally {
      setAccepting(false);
    }
  };

  const emailMatches = user && invite && user.email?.toLowerCase() === invite.email?.toLowerCase();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
      <div className="mb-6"><BrandLogo variant="on-white" className="h-8" /></div>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-700" /> Co-Admin Invitation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-slate-500" /></div>
          ) : error ? (
            <div className="text-center py-4">
              <XCircle className="h-8 w-8 text-rose-500 mx-auto mb-2" />
              <p className="text-slate-700">{error}</p>
              <Link to="/" className="text-emerald-700 text-sm underline mt-3 inline-block">Go home</Link>
            </div>
          ) : invite.status !== 'pending' ? (
            <div className="text-center py-4">
              <XCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              <p className="text-slate-700">This co-admin invite is no longer valid ({invite.status}).</p>
              <Link to="/login" className="text-emerald-700 text-sm underline mt-3 inline-block">Go to login</Link>
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-700">
                <strong>{invite.invited_by_name || 'A school administrator'}</strong> invited
                <strong> {invite.email}</strong> to be a <strong>co-admin</strong> of
                <strong> {invite.school_name}</strong>.
              </p>
              <p className="text-xs text-slate-500">
                Co-admins can post positions, manage subs, and view analytics. They cannot change billing or manage other co-admins.
              </p>

              {emailMatches ? (
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={handleAccept} disabled={accepting} data-testid="accept-co-admin-invite-btn">
                  {accepting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                  Accept co-admin invite
                </Button>
              ) : user ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                  You&apos;re signed in as <strong>{user.email}</strong>, but this invite was sent to
                  <strong> {invite.email}</strong>. Please sign in with that email to accept.
                </div>
              ) : invite.account_exists ? (
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`/login?next=${encodeURIComponent('/accept-invite?token=' + token)}`)}>
                  Log in to accept
                </Button>
              ) : (
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate(`/register?invite_token=${token}`)}>
                  Create your account to accept
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
